/**
 * Tree → HTML string compiler.
 *
 * The string-emitting twin of `render/toReact.tsx`. Same dispatch
 * table; same per-primitive emit logic; output is a single HTML string
 * the iframe runtime morphs into the live document body, and that the
 * publish-time emitter writes to a customer's hosted page.
 *
 * Two callers:
 *
 *   1. Editor preview iframe (Phase 2). The parent posts the tree;
 *      the iframe runtime calls `compileTreeToHtml` and morphdom-diffs
 *      the result against the live DOM. Re-renders are cheap because
 *      morphdom only patches what changed.
 *
 *   2. Publish-time HTML emitter (Phase 2 / Phase 3). Server-side
 *      writes the same string into a Cloudflare Pages bundle so the
 *      published page renders identically without React present.
 *
 * Why a separate compiler instead of `renderToStaticMarkup` over the
 * React tree:
 *
 *   - The iframe runtime is intentionally React-free. Bundling
 *     react+react-dom into the preview iframe doubles the payload for
 *     no rendering benefit — every primitive emits boring HTML.
 *   - The published page also doesn't need React. Cheap shipping.
 *   - We get to control attribute emission tightly: `data-oj-id`,
 *     `data-oj-action`, `data-oj-literal`, `data-oj-component` all
 *     have to be present and exact for the runtime delegates to find
 *     them. React's renderer escapes underscores in some attribute
 *     names; better to own the encoding.
 *
 * What gets emitted:
 *
 *   - Layout primitives → `<section>` / `<div>` with style attrs and
 *     recursive child emission.
 *   - Content leaves → `<h1>`–`<h6>`, `<p>`, `<img>`, `<button>`/`<a>`,
 *     `<video>`, `<hr>`, etc.
 *   - SDK widgets → real custom-element tags (`<oj-pay>`, etc.) with
 *     their props as attributes. The `oj-elements.ts` script (PR15
 *     work) defines these custom elements and runs both in the editor
 *     iframe and on the published page.
 *   - Element escape hatch → the requested tag if allowed, `<div>` otherwise.
 *
 * Security:
 *
 *   - Every text-node value (`text`, `content`, `alt`, `title`, etc.)
 *     is HTML-escaped via `escapeText`.
 *   - Every attribute value is escaped via `escapeAttr`.
 *   - URLs in `href` / `src` are screened by `isSafeUrl` — `javascript:`,
 *     `data:` (except images), and `file:` are stripped.
 *   - `tag` for the Element escape hatch is intersected with the same
 *     `ALLOWED_TAGS` allowlist as the React renderer.
 *
 * The compiler does not build a tokenized DOM; it emits strings
 * directly. That's the right shape for morphdom (which parses an HTML
 * string into a node tree internally) and for the publish-time
 * emitter.
 */

import { resolveInstanceStyles } from '../styleResolution';
import type {
  Instance,
  InstanceType,
  LandingPageData,
  NodeId,
} from '../types';

// ─────────────────────────────────────────────────────────────────────────
// Public entry
// ─────────────────────────────────────────────────────────────────────────

/**
 * Compile the full tree to an HTML string starting at `data.rootId`.
 *
 * Returns the empty string if the root instance is missing — better
 * than a partial render that hides the bug. The caller (iframe runtime
 * or publish-time emitter) is expected to surface a placeholder or
 * error in that case.
 */
export function compileTreeToHtml(data: LandingPageData): string {
  if (!data.instances.has(data.rootId)) return '';
  return compileNode(data, data.rootId);
}

/**
 * Compile a single subtree. Exported so callers (the inspector preview,
 * future fragment-level streaming) can render arbitrary nodes.
 */
export function compileNode(data: LandingPageData, instanceId: NodeId): string {
  const instance = data.instances.get(instanceId);
  if (!instance) return '';

  const layout = LAYOUT_EMITTERS[instance.type];
  if (layout) {
    return layout(data, instance);
  }
  const leaf = LEAF_EMITTERS[instance.type];
  if (leaf) {
    return leaf(data, instance);
  }
  // Unknown instance type — render a labeled placeholder so the bug is
  // visible rather than producing silent gaps.
  return `<div data-oj-id="${escapeAttr(instanceId)}" style="padding:16px;border:2px dashed crimson;color:crimson;font-family:monospace;font-size:12px">Missing emitter: ${escapeText(instance.type)}</div>`;
}

// ─────────────────────────────────────────────────────────────────────────
// Layout emitters
// ─────────────────────────────────────────────────────────────────────────

type Emit = (data: LandingPageData, instance: Instance) => string;

const LAYOUT_EMITTERS: Partial<Record<InstanceType, Emit>> = {
  section: emitSection,
  stack: emitStack,
  row: emitRow,
  grid: emitGrid,
  container: emitContainer,
};

function emitSection(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const baseStyle: Record<string, string> = { width: '100%' };
  if (typeof props.padding === 'string') baseStyle.padding = props.padding;
  if (typeof props.background === 'string') baseStyle.background = props.background;
  if (typeof props.minHeight === 'string') baseStyle['min-height'] = props.minHeight;

  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  const inner = instance.children.map((id) => compileNode(data, id)).join('');
  const maxWidth = typeof props.maxWidth === 'string' ? props.maxWidth : null;
  const innerWrapped = maxWidth
    ? `<div style="max-width:${escapeAttr(maxWidth)};margin-inline:auto">${inner}</div>`
    : inner;

  return `<section${dataAttrs}${styleAttr}>${scopedCss}${innerWrapped}</section>`;
}

function emitStack(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const baseStyle: Record<string, string> = {
    display: 'flex',
    'flex-direction': 'column',
  };
  if (typeof props.gap === 'string') baseStyle.gap = props.gap;
  const align = alignToFlex(props.align);
  if (align) baseStyle['align-items'] = align;
  const justify = justifyToFlex(props.justify);
  if (justify) baseStyle['justify-content'] = justify;
  if (typeof props.padding === 'string') baseStyle.padding = props.padding;

  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  const inner = instance.children.map((id) => compileNode(data, id)).join('');
  return `<div${dataAttrs}${styleAttr}>${scopedCss}${inner}</div>`;
}

function emitRow(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const wrap = props.wrap !== false;
  const baseStyle: Record<string, string> = {
    display: 'flex',
    'flex-direction': 'row',
    'flex-wrap': wrap ? 'wrap' : 'nowrap',
  };
  if (typeof props.gap === 'string') baseStyle.gap = props.gap;
  const align = alignToFlex(props.align);
  if (align) baseStyle['align-items'] = align;
  const justify = justifyToFlex(props.justify);
  if (justify) baseStyle['justify-content'] = justify;
  if (typeof props.padding === 'string') baseStyle.padding = props.padding;

  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  const inner = instance.children.map((id) => compileNode(data, id)).join('');
  return `<div${dataAttrs}${styleAttr}>${scopedCss}${inner}</div>`;
}

function emitGrid(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const columns = (props.columns as number | 'auto') ?? 'auto';
  const minColWidth = typeof props.minColWidth === 'string' ? props.minColWidth : '240px';
  const gridTemplateColumns =
    columns === 'auto'
      ? `repeat(auto-fit, minmax(${minColWidth}, 1fr))`
      : `repeat(${columns}, 1fr)`;

  const baseStyle: Record<string, string> = {
    display: 'grid',
    'grid-template-columns': gridTemplateColumns,
  };
  if (typeof props.gap === 'string') baseStyle.gap = props.gap;
  if (typeof props.padding === 'string') baseStyle.padding = props.padding;

  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  const inner = instance.children.map((id) => compileNode(data, id)).join('');
  return `<div${dataAttrs}${styleAttr}>${scopedCss}${inner}</div>`;
}

function emitContainer(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const baseStyle: Record<string, string> = { 'margin-inline': 'auto' };
  if (typeof props.maxWidth === 'string') baseStyle['max-width'] = props.maxWidth;
  if (typeof props.padding === 'string') baseStyle.padding = props.padding;

  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  const inner = instance.children.map((id) => compileNode(data, id)).join('');
  return `<div${dataAttrs}${styleAttr}>${scopedCss}${inner}</div>`;
}

// ─────────────────────────────────────────────────────────────────────────
// Leaf emitters
// ─────────────────────────────────────────────────────────────────────────

const LEAF_EMITTERS: Partial<Record<InstanceType, Emit>> = {
  heading: emitHeading,
  text: emitText,
  image: emitImage,
  button: emitButton,
  video: emitVideo,
  logo: emitLogo,
  spacer: emitSpacer,
  divider: emitDivider,
  element: emitElement,
  'custom-html': emitCustomHtml,
  'oj-link-account-button': emitSdkComponent('oj-link-account-button'),
  'oj-pay': emitSdkComponent('oj-pay'),
  'oj-redeem': emitSdkComponent('oj-redeem'),
  'oj-rewards-grid': emitSdkComponent('oj-rewards-grid'),
  'oj-account': emitSdkComponent('oj-account'),
  'oj-exchange': emitSdkComponent('oj-exchange'),
  'oj-form-input': emitSdkComponent('oj-form-input'),
  'oj-form-submit': emitSdkComponent('oj-form-submit'),
};

function emitHeading(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const text = typeof props.text === 'string' ? props.text : '';
  const level = clampHeadingLevel(props.level);
  const baseStyle: Record<string, string> = { margin: '0' };
  const align = alignToCss(props.align);
  if (align) baseStyle['text-align'] = align;
  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  return `<h${level}${dataAttrs}${styleAttr}>${scopedCss}${escapeText(text)}</h${level}>`;
}

function emitText(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const content = typeof props.content === 'string' ? props.content : '';
  const baseStyle: Record<string, string> = { margin: '0' };
  const align = alignToCss(props.align);
  if (align) baseStyle['text-align'] = align;
  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  return `<p${dataAttrs}${styleAttr}>${scopedCss}${escapeText(content)}</p>`;
}

function emitImage(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const src = isSafeUrl(props.src, { allowDataImage: true })
    ? (props.src as string)
    : '';
  const alt = typeof props.alt === 'string' ? props.alt : '';
  const baseStyle: Record<string, string> = {
    width: typeof props.width === 'string' ? props.width : '100%',
    height: typeof props.height === 'string' ? props.height : 'auto',
    'object-fit': typeof props.objectFit === 'string' ? props.objectFit : 'cover',
    display: 'block',
  };
  if (typeof props.radius === 'string') baseStyle['border-radius'] = props.radius;
  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  return `${scopedCss}<img${dataAttrs} src="${escapeAttr(src)}" alt="${escapeAttr(alt)}"${styleAttr}/>`;
}

function emitButton(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const text = typeof props.text === 'string' ? props.text : 'Button';
  const variant = stringEnum(props.variant, ['primary', 'secondary', 'outline', 'ghost'], 'primary');
  const size = stringEnum(props.size, ['sm', 'md', 'lg'], 'md');
  const shape = stringEnum(props.shape, ['rounded', 'pill', 'square'], 'rounded');
  const fullWidth = props.fullWidth === true;
  const onClick =
    props.onClick && typeof props.onClick === 'object'
      ? (props.onClick as { type: string; [k: string]: unknown })
      : undefined;

  const baseStyle: Record<string, string> = {
    display: fullWidth ? 'block' : 'inline-flex',
    'align-items': 'center',
    'justify-content': 'center',
    gap: '8px',
    'font-weight': '600',
    cursor: 'pointer',
    'text-decoration': 'none',
    border: 'none',
    transition: 'opacity 0.15s ease',
    ...buttonVariantStyle(variant),
    ...buttonSizeStyle(size),
    ...buttonShapeStyle(shape),
  };
  if (fullWidth) baseStyle.width = '100%';

  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  const actionAttr = onClick
    ? ` data-oj-action="${escapeAttr(JSON.stringify(onClick))}"`
    : '';

  if (onClick && onClick.type === 'navigate') {
    const url = typeof onClick.url === 'string' ? onClick.url : '#';
    const safeUrl = isSafeUrl(url) ? url : '#';
    const isExternal = /^https?:/i.test(safeUrl);
    const targetAttr = isExternal ? ` target="_blank" rel="noopener noreferrer"` : '';
    return `${scopedCss}<a${dataAttrs} href="${escapeAttr(safeUrl)}"${targetAttr}${actionAttr}${styleAttr}>${escapeText(text)}</a>`;
  }

  return `${scopedCss}<button type="button"${dataAttrs}${actionAttr}${styleAttr}>${escapeText(text)}</button>`;
}

function emitVideo(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const src = isSafeUrl(props.src) ? (props.src as string) : '';
  const poster = isSafeUrl(props.poster, { allowDataImage: true })
    ? (props.poster as string)
    : '';
  const autoplay = props.autoplay === true;
  const controls = props.controls !== false;
  const loop = props.loop === true;
  const muted = props.muted === true || autoplay;

  const baseStyle: Record<string, string> = {
    width: '100%',
    height: 'auto',
    display: 'block',
  };
  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  const attrs =
    (autoplay ? ' autoplay' : '') +
    (controls ? ' controls' : '') +
    (loop ? ' loop' : '') +
    (muted ? ' muted' : '') +
    (autoplay ? ' playsinline' : '');
  const posterAttr = poster ? ` poster="${escapeAttr(poster)}"` : '';
  return `${scopedCss}<video${dataAttrs} src="${escapeAttr(src)}"${posterAttr}${attrs}${styleAttr}></video>`;
}

function emitLogo(data: LandingPageData, instance: Instance): string {
  // Phase 1 renders a labeled placeholder (no resolver wired yet); we
  // mirror that here so the iframe preview matches the React preview.
  // Phase 2 will wire a resolver via tree-update message context.
  const props = data.props.get(instance.id) ?? {};
  const brandRef = typeof props.brandRef === 'string' ? props.brandRef : 'self';
  const size = typeof props.size === 'string' ? props.size : 'md';
  const dimensions = logoSizeStyle(size);

  const baseStyle: Record<string, string> = {
    ...dimensions,
    'object-fit': 'contain',
    display: 'inline-flex',
    'align-items': 'center',
    'justify-content': 'center',
    background: 'var(--oj-color-surface-2, #f3f4f6)',
    border: '1px dashed var(--oj-color-border, #d1d5db)',
    'font-size': '12px',
    color: 'var(--oj-color-text-muted, #6b7280)',
    padding: '8px',
  };
  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  return `${scopedCss}<div${dataAttrs}${styleAttr}>${escapeText(brandRef)}</div>`;
}

function emitSpacer(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const size = typeof props.size === 'string' ? props.size : '24px';
  const axis = props.axis === 'horizontal' ? 'horizontal' : 'vertical';
  const baseStyle: Record<string, string> = { 'flex-shrink': '0' };
  if (axis === 'vertical') {
    baseStyle.height = size;
    baseStyle.width = '100%';
  } else {
    baseStyle.width = size;
    baseStyle.height = '100%';
  }
  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  return `${scopedCss}<div${dataAttrs} aria-hidden="true"${styleAttr}></div>`;
}

function emitDivider(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const axis = props.axis === 'vertical' ? 'vertical' : 'horizontal';
  const thickness = typeof props.thickness === 'string' ? props.thickness : '1px';
  const color =
    typeof props.color === 'string' ? props.color : 'var(--oj-color-border, #e5e7eb)';

  const baseStyle: Record<string, string> = { border: 'none', 'flex-shrink': '0' };
  if (axis === 'horizontal') {
    baseStyle.width = '100%';
    baseStyle.height = thickness;
    baseStyle.background = color;
    baseStyle.margin = '0';
  } else {
    baseStyle.width = thickness;
    baseStyle.height = '100%';
    baseStyle.background = color;
    baseStyle['align-self'] = 'stretch';
  }

  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  if (axis === 'horizontal') {
    return `${scopedCss}<hr${dataAttrs}${styleAttr}/>`;
  }
  return `${scopedCss}<div${dataAttrs} role="separator" aria-orientation="vertical"${styleAttr}></div>`;
}

function emitElement(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const requestedTag =
    typeof props.tag === 'string' ? props.tag.toLowerCase() : 'div';
  const tag = ALLOWED_ELEMENT_TAGS.has(requestedTag) ? requestedTag : 'div';
  const text = typeof props.text === 'string' ? props.text : '';

  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, {});

  let extra = '';
  if (tag === 'a') {
    if (typeof props.href === 'string' && isSafeUrl(props.href)) {
      extra += ` href="${escapeAttr(props.href as string)}"`;
    }
    if (typeof props.target === 'string') extra += ` target="${escapeAttr(props.target as string)}"`;
    if (typeof props.rel === 'string') extra += ` rel="${escapeAttr(props.rel as string)}"`;
  }
  return `<${tag}${dataAttrs}${extra}${styleAttr}>${scopedCss}${escapeText(text)}</${tag}>`;
}

/**
 * `custom-html` leaf — embeds the customer's HTML inside a sandboxed
 * iframe via `srcdoc`. This compiler runs in two contexts:
 *
 *   - Editor iframe canvas (Phase 2): HTML compiled here is morphed
 *     into the iframe document. The customer's iframe-in-iframe
 *     setup works because all browsers allow nested sandboxed
 *     iframes.
 *   - Publish-time emitter: same string lands on Cloudflare Pages.
 *     `sandbox="allow-same-origin"` (no `allow-scripts`) means the
 *     embedded HTML can use CSS but cannot execute JS in the
 *     published page's context.
 *
 * The compiler does NOT run DOMPurify here — sanitization happens at
 * write time in `applyToolCall.ts`. We trust the saved tree because
 * the sanitizer ran when the value was set. If a future code path
 * lets unsanitized HTML reach this emitter, the sandboxed iframe is
 * the second line of defense.
 *
 * The `srcdoc` value is encoded for an HTML attribute. We use an
 * extra-strict escape that handles the four characters that would
 * break out of the attribute: `"`, `&`, `<`, `>`. The browser
 * decodes them on parse so the iframe doc sees the original markup.
 */
function emitCustomHtml(data: LandingPageData, instance: Instance): string {
  const props = data.props.get(instance.id) ?? {};
  const html = typeof props.html === 'string' ? props.html : '';
  const baseStyle: Record<string, string> = {
    display: 'block',
    width: '100%',
  };
  const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, baseStyle);
  // Wrap the customer's HTML in a minimal document so iframe content
  // gets a body. We forward brand tokens at publish time via the
  // page-level `<style>` block (rendered separately by the page
  // emitter); the iframe inherits via `:root` cascading at the
  // document level isn't possible across iframes, so the publish-time
  // emitter will inline a token snapshot here in a follow-up PR.
  const srcDoc =
    `<!DOCTYPE html><html><head><meta charset="utf-8"><style>` +
    `html,body{margin:0;padding:0;font-family:var(--oj-font-body,system-ui,sans-serif);color:var(--oj-color-text,#111827);}` +
    `</style></head><body>${html}</body></html>`;
  const iframeStyle = 'width:100%;border:none;display:block;min-height:200px;';
  return `${scopedCss}<div${dataAttrs}${styleAttr}><iframe title="Custom HTML embed" sandbox="allow-same-origin" srcdoc="${escapeAttr(srcDoc)}" style="${iframeStyle}"></iframe></div>`;
}

/**
 * SDK widgets emit the real custom-element tag (`<oj-pay>` etc.). The
 * `oj-elements.js` script (PR15) defines these as Custom Elements and
 * runs both in the editor iframe and on the published page, so the
 * rendered HTML is identical in both contexts. Until that script lands
 * the iframe will treat them as unknown elements and they'll render as
 * empty inline spans — which is acceptable as a fallback (no broken
 * layout). Editor users still see the React preview's labeled
 * placeholder via the React renderer; the iframe runtime only kicks in
 * once the iframe canvas is wired.
 */
function emitSdkComponent(tag: InstanceType): Emit {
  return (data, instance) => {
    const props = data.props.get(instance.id) ?? {};
    const { styleAttr, scopedCss, dataAttrs } = primitiveAttrs(data, instance.id, {});

    // Forward the well-known props as data-* attributes. The custom
    // element reads its own `data-oj-*` attrs on connection.
    let propAttrs = '';
    for (const [key, value] of Object.entries(props)) {
      if (typeof value === 'string') {
        propAttrs += ` data-oj-${escapeAttr(toKebab(key))}="${escapeAttr(value)}"`;
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        propAttrs += ` data-oj-${escapeAttr(toKebab(key))}="${escapeAttr(String(value))}"`;
      }
      // Object-typed props (action descriptors, etc.) are JSON-encoded
      // so the custom element can pull them out of the dataset.
      else if (value && typeof value === 'object') {
        propAttrs += ` data-oj-${escapeAttr(toKebab(key))}="${escapeAttr(JSON.stringify(value))}"`;
      }
    }
    return `${scopedCss}<${tag}${dataAttrs}${propAttrs}${styleAttr}></${tag}>`;
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Shared attribute computation
// ─────────────────────────────────────────────────────────────────────────

interface PrimitiveAttrs {
  /** Pre-built ` style="..."` attribute fragment (leading space included
   *  so it can be concatenated raw). Empty string when there are no
   *  styles at all, so we don't emit `<div style="">`. */
  styleAttr: string;
  /** Pre-built `<style>...</style>` block for breakpoint/state CSS,
   *  empty string when there are no scoped rules. */
  scopedCss: string;
  /** Pre-built ` data-oj-id="..."` (and optional ` data-oj-literal="true"`)
   *  attribute fragment with leading space. */
  dataAttrs: string;
}

function primitiveAttrs(
  data: LandingPageData,
  instanceId: NodeId,
  baseStyle: Record<string, string>,
): PrimitiveAttrs {
  const resolved = resolveInstanceStyles(data, instanceId);

  // Merge baseStyle (kebab-case) with resolved.inline (camelCase) into a
  // unified kebab-case map so we can serialize once.
  const flat: Record<string, string> = {};
  for (const [k, v] of Object.entries(baseStyle)) {
    if (v != null && v !== '') flat[k] = v;
  }
  for (const [k, v] of Object.entries(resolved.inline)) {
    if (v != null && v !== '') flat[toKebab(k)] = v;
  }

  const styleString = Object.entries(flat)
    .map(([k, v]) => `${k}:${v}`)
    .join(';');
  const styleAttr = styleString ? ` style="${escapeAttr(styleString)}"` : '';

  const scopedCss = resolved.css ? `<style>${resolved.css}</style>` : '';

  let dataAttrs = ` data-oj-id="${escapeAttr(instanceId)}"`;
  if (resolved.hasLiterals) dataAttrs += ` data-oj-literal="true"`;

  return { styleAttr, scopedCss, dataAttrs };
}

// ─────────────────────────────────────────────────────────────────────────
// Style helpers (mirrors the React renderers)
// ─────────────────────────────────────────────────────────────────────────

function alignToFlex(value: unknown): string | null {
  switch (value) {
    case 'start':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'end':
      return 'flex-end';
    case 'stretch':
      return 'stretch';
    default:
      return null;
  }
}

function justifyToFlex(value: unknown): string | null {
  switch (value) {
    case 'start':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'end':
      return 'flex-end';
    case 'between':
      return 'space-between';
    case 'around':
      return 'space-around';
    case 'evenly':
      return 'space-evenly';
    default:
      return null;
  }
}

function alignToCss(value: unknown): string | null {
  switch (value) {
    case 'left':
    case 'center':
    case 'right':
    case 'justify':
      return value as string;
    default:
      return null;
  }
}

function clampHeadingLevel(value: unknown): 1 | 2 | 3 | 4 | 5 | 6 {
  const n = typeof value === 'string' ? parseInt(value, 10) : value;
  if (typeof n === 'number' && n >= 1 && n <= 6) return n as 1 | 2 | 3 | 4 | 5 | 6;
  return 2;
}

function buttonVariantStyle(variant: 'primary' | 'secondary' | 'outline' | 'ghost'): Record<string, string> {
  switch (variant) {
    case 'primary':
      return {
        background: 'var(--oj-color-primary, #3b82f6)',
        color: 'var(--oj-color-on-primary, #ffffff)',
      };
    case 'secondary':
      return {
        background: 'transparent',
        color: 'var(--oj-color-primary, #3b82f6)',
        border: '2px solid var(--oj-color-primary, #3b82f6)',
      };
    case 'outline':
      return {
        background: 'transparent',
        color: 'var(--oj-color-primary, #3b82f6)',
        border: '1px solid var(--oj-color-primary, #3b82f6)',
      };
    case 'ghost':
      return {
        background: 'transparent',
        color: 'var(--oj-color-primary, #3b82f6)',
      };
  }
}

function buttonSizeStyle(size: 'sm' | 'md' | 'lg'): Record<string, string> {
  switch (size) {
    case 'sm':
      return { padding: '8px 16px', 'font-size': '14px' };
    case 'md':
      return { padding: '12px 24px', 'font-size': '16px' };
    case 'lg':
      return { padding: '16px 32px', 'font-size': '18px' };
  }
}

function buttonShapeStyle(shape: 'rounded' | 'pill' | 'square'): Record<string, string> {
  switch (shape) {
    case 'rounded':
      return { 'border-radius': '8px' };
    case 'pill':
      return { 'border-radius': '9999px' };
    case 'square':
      return { 'border-radius': '0' };
  }
}

function logoSizeStyle(size: string): Record<string, string> {
  switch (size) {
    case 'sm':
      return { width: '80px', height: '40px' };
    case 'md':
      return { width: '120px', height: '60px' };
    case 'lg':
      return { width: '180px', height: '90px' };
    default:
      return { width: size, height: 'auto' };
  }
}

// ─────────────────────────────────────────────────────────────────────────
// String helpers
// ─────────────────────────────────────────────────────────────────────────

/** Escape user-provided text for HTML body content. */
function escapeText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Escape user-provided text for an HTML attribute value (double-quoted). */
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** True iff `value` is a string URL we'll allow in an attribute. Blocks
 *  `javascript:`, `data:` (except data:image/* when explicitly allowed
 *  for img/poster), `vbscript:`, `file:`. Allows http(s):, mailto:,
 *  tel:, and root-relative / origin-relative paths. */
function isSafeUrl(value: unknown, opts: { allowDataImage?: boolean } = {}): boolean {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  // Strip any leading whitespace + control chars before checking the
  // scheme — `\tjavascript:...` is the classic XSS bypass.
  // eslint-disable-next-line no-control-regex
  const cleaned = trimmed.replace(/[\x00-\x20\x7f]/g, '');
  if (/^javascript:/i.test(cleaned)) return false;
  if (/^vbscript:/i.test(cleaned)) return false;
  if (/^file:/i.test(cleaned)) return false;
  if (/^data:/i.test(cleaned)) {
    if (opts.allowDataImage && /^data:image\/[a-z0-9.+-]+;/i.test(cleaned)) {
      return true;
    }
    return false;
  }
  return true;
}

/** Convert a camelCase JS key to its kebab-case CSS / HTML twin. */
function toKebab(key: string): string {
  return key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

/** Coerce `value` to one of the listed string options, falling back to
 *  `fallback` if it's not in the set. Lets us treat user/LLM-supplied
 *  prop values as untrusted strings rather than narrowing types
 *  upstream. */
function stringEnum<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  if (typeof value === 'string' && (options as readonly string[]).includes(value)) {
    return value as T;
  }
  return fallback;
}

// Mirror of the React renderer's allowlist. Kept separate so the
// compiler doesn't import from `renderers/element.tsx` (which would
// pull React in).
const ALLOWED_ELEMENT_TAGS: ReadonlySet<string> = new Set([
  'div',
  'span',
  'aside',
  'article',
  'figure',
  'figcaption',
  'nav',
  'header',
  'footer',
  'main',
  'address',
  'blockquote',
  'cite',
  'code',
  'em',
  'strong',
  'small',
  'sub',
  'sup',
  'mark',
  'pre',
  'time',
  'kbd',
  'abbr',
  'dl',
  'dt',
  'dd',
  'ul',
  'ol',
  'li',
  'a',
  'br',
]);
